import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { queryKeys } from '../../../../config/queryKeys'
import {
  getScheduleStandingsList,
  type StandingsListQuery,
  type StandingsListRow,
} from '../../../../services/competition/getScheduleStandings'
import { COMPETITION_STATS_STALE_TIME } from '../../constants'
import {
  groupStandingsBySchedule,
  type StandingsGroup,
} from '../utils/groupStandingsBySchedule'

interface StandingsListResult {
  rows: StandingsListRow[]
  groups: Record<string, StandingsGroup<StandingsListRow>>
}

/** Module-level so React Query can keep the selected value between renders. */
const selectStandings = (rows: StandingsListRow[]): StandingsListResult => ({
  rows,
  groups: groupStandingsBySchedule(rows),
})

interface UseScheduleStandingsListArgs {
  scheduleId: string | null | undefined
  query: StandingsListQuery
  /** The Schedule page's Refresh/reprocess counter — a bump refetches. */
  refreshKey?: unknown
  /** False until the tab knows its grouping and default sort. */
  enabled: boolean
}

/**
 * The Schedule page's Standings tab data, cached per schedule + grouping +
 * sort. Changing the sort or the division keeps the previous table on screen
 * (dimmed by the caller via `isFetching`) until the new one lands, instead of
 * blanking it.
 */
export const useScheduleStandingsList = ({
  scheduleId,
  query,
  refreshKey,
  enabled,
}: UseScheduleStandingsListArgs) => {
  const result = useQuery({
    queryKey: queryKeys.scheduleStandings.list(scheduleId, query, refreshKey),
    queryFn: () => getScheduleStandingsList(scheduleId as string, query),
    enabled: enabled && Boolean(scheduleId),
    staleTime: COMPETITION_STATS_STALE_TIME,
    placeholderData: keepPreviousData,
    select: selectStandings,
  })

  return {
    rows: result.data?.rows,
    groups: result.data?.groups,
    isFetching: result.isFetching,
    error: result.error,
  }
}
